<script lang="ts">
	import { afterNavigate } from '$app/navigation';
	import { onMount, setContext } from 'svelte';
	import * as Sheet from '../ui/sheet/index';

	interface Props {
		isVisible?: boolean;
		title?: string;
		isMobile?: boolean;
		sheetOpen?: boolean;
		onsheetclosed?: () => void;
		onsheetopened?: () => void;
		children?: import('svelte').Snippet;
	}

	let {
		isVisible = false,
		title = '',
		isMobile = true,
		sheetOpen = $bindable(false),
		onsheetclosed,
		onsheetopened,
		children
	}: Props = $props();

	let touchStartX = 0;
	let touchStartY = 0;
	let isDragging = false;
	let dragDistance = 0;

	const SWIPE_THRESHOLD = 100; // Minimum distance to trigger swipe

	const closeSheet = () => {
		if (!sheetOpen) return;
		sheetOpen = false;
		onsheetclosed?.();
	};

	// Debug logging
	$effect(() => {
		console.log('MobilePaneGroup render:', { isMobile, sheetOpen, isVisible, title });
	});

	// Handle touch events for swipe gestures
	function handleTouchStart(event: TouchEvent) {
		if (!isMobile || !sheetOpen) return;

		touchStartX = event.touches[0].clientX;
		touchStartY = event.touches[0].clientY;
		isDragging = false;
		dragDistance = 0;
	}

	function handleTouchMove(event: TouchEvent) {
		if (!isMobile || !sheetOpen) return;

		const touchX = event.touches[0].clientX;
		const touchY = event.touches[0].clientY;
		const deltaX = touchX - touchStartX;
		const deltaY = touchY - touchStartY;

		// Only handle horizontal swipes
		if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
			isDragging = true;
			dragDistance = deltaX;

			// Prevent default to avoid page scrolling
			event.preventDefault();
		}
	}

	function handleTouchEnd(_event: TouchEvent) {
		if (!isMobile || !sheetOpen || !isDragging) return;

		// If swiped left (negative deltaX) and distance is sufficient, close the sheet
		if (dragDistance < -SWIPE_THRESHOLD) {
			closeSheet();
		}

		isDragging = false;
		dragDistance = 0;
	}

	// Handle left edge gesture to open sheet
	function handleLeftEdgeGesture(_event: TouchEvent) {
		if (sheetOpen) return;

		const touchX = _event.touches[0].clientX;

		// If touch starts within 20px of left edge, open sheet
		if (touchX <= 20) {
			if (handleLeftScreenSlideOpen) {
				handleLeftScreenSlideOpen(new CustomEvent('itemSelected', { detail: _event }));
			}
		}
	}

	setContext('handleLeftEdgeGesture', handleLeftEdgeGesture);
	setContext('handleTouchStart', handleTouchStart);
	setContext('handleTouchMove', handleTouchMove);
	setContext('handleTouchEnd', handleTouchEnd);

	// Watch for visibility changes
	$effect(() => {
		if (!isVisible) {
			sheetOpen = false;
		}
	});

	// Listen for custom DOM events
	onMount(() => {
		document.addEventListener('itemSelected', handleItemSelected as EventListener);
		return () => {
			document.removeEventListener('itemSelected', handleItemSelected as EventListener);
		};
	});

	const handleLeftScreenSlideOpen: (event: CustomEvent<TouchEvent>) => void = (_event) => {
		sheetOpen = true;
		onsheetopened?.();
	};
	const handleSheetCloseClick: (event: CustomEvent<TouchEvent>) => void = (_event) => {
		closeSheet();
	};
	const handleItemSelected: (event: CustomEvent<any>) => void = (_event) => {
		closeSheet();
	};
	const handleHamburgerClick: (event: CustomEvent<TouchEvent>) => void = (_event) => {
		sheetOpen = true;
		onsheetopened?.();
		console.log('Got hamburger click', _event);
	};

	setContext('handleLeftScreenSlideOpen', handleLeftScreenSlideOpen);
	setContext('handleSheetCloseClick', handleSheetCloseClick);
	setContext('handleItemSelected', handleItemSelected);
	setContext('handleHamburgerClick', handleHamburgerClick);

	setContext('asSheet', true);

	afterNavigate(() => {
		closeSheet();
	});
</script>

<!-- Left edge gesture detector -->
<div
	class="fixed left-0 top-0 z-40 h-full w-5 touch-none"
	ontouchstart={handleLeftEdgeGesture}
></div>

<!-- Sheet for mobile layout -->
<Sheet.Root
	bind:open={sheetOpen}
	onOpenChange={(open: boolean) => {
		sheetOpen = open;
		if (!open) {
			onsheetclosed?.();
		} else {
			onsheetopened?.();
		}
	}}
>
	{@render children?.()}
</Sheet.Root>
